import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, AreaChart, Area } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Leaf, Droplet, Wind, Trash2, Sparkles } from 'lucide-react';
import MetricCard from '../components/MetricCard';

const mockData = {
  carbonEmissions: [
    { year: '2018', emissions: 1000 },
    { year: '2019', emissions: 950 },
    { year: '2020', emissions: 880 },
    { year: '2021', emissions: 800 },
    { year: '2022', emissions: 750 },
  ],
  resourceUsage: [
    { resource: 'Water', usage: 500, target: 450 },
    { resource: 'Electricity', usage: 300, target: 280 },
    { resource: 'Paper', usage: 50, target: 40 },
    { resource: 'Fuel', usage: 200, target: 180 },
  ],
  yearOverYear: [
    { year: '2018', carbon: 1000, water: 550, renewable: 20, waste: 15 },
    { year: '2019', carbon: 950, water: 540, renewable: 23, waste: 16 },
    { year: '2020', carbon: 880, water: 530, renewable: 26, waste: 17 },
    { year: '2021', carbon: 800, water: 520, renewable: 30, waste: 18 },
    { year: '2022', carbon: 750, water: 500, renewable: 35, waste: 20 },
  ],
};

const Environmental = () => (
  <div className="p-6">
    <h1 className="text-3xl font-bold mb-6">Environmental Metrics</h1>
   
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <MetricCard title="Carbon Footprint" value="750 tons" trend={-6.25} icon={Leaf} />
      <MetricCard title="Water Usage" value="500 kL" trend={-2.5} icon={Droplet} />
      <MetricCard title="Renewable Energy" value="35%" trend={5} icon={Wind} />
      <MetricCard title="Waste Reduction" value="20%" trend={3} icon={Trash2} />
    </div>

    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Carbon Emissions Trend</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={mockData.carbonEmissions}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="emissions" stroke="#8884d8" activeDot={{ r: 8 }} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>

    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Year-over-Year Comparison</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={mockData.yearOverYear}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="carbon" stackId="1" stroke="#8884d8" fill="#8884d8" />
            <Area type="monotone" dataKey="water" stackId="1" stroke="#82ca9d" fill="#82ca9d" />
            <Area type="monotone" dataKey="renewable" stackId="1" stroke="#ffc658" fill="#ffc658" />
            <Area type="monotone" dataKey="waste" stackId="1" stroke="#ff8042" fill="#ff8042" />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>

    <Card className="mt-6">
      <CardHeader>
        <CardTitle>Resource Usage vs Target</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={mockData.resourceUsage}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="resource" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="usage" fill="#8884d8" name="Current Usage" />
            <Bar dataKey="target" fill="#82ca9d" name="Target" />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>

    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Sparkles className="mr-2 h-5 w-5 text-yellow-500" />
          AI-Generated Environmental Insights
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="list-disc pl-5 space-y-2">
          <li>Carbon emissions have decreased by 6.25% this year, putting you on track to meet your 2030 net-zero goal.</li>
          <li>Water usage efficiency has improved, but there's potential for further reduction in manufacturing processes.</li>
          <li>The increase in renewable energy usage has offset 15% of your total energy costs.</li>
          <li>Your waste reduction efforts have positively impacted your supply chain sustainability score.</li>
        </ul>
      </CardContent>
    </Card>
  </div>
);

export default Environmental;